import React, { ComponentProps } from 'react'
import { Well } from '@pushly/aqe/lib/components'
import { ScheduleWindow } from '@pushly/models/lib/enums/schedule-window'
import { getClassNames } from '../../../_utils/classnames'
import { BASE_TIME_FORMAT_WITHOUT_TZ } from '../../../constants'
import { useAppMessageBuilderContext } from '../../../features/app-messages/context'
import { MutableAppMessageBuilderSchedulePayload } from '../model-extensions'
import { BetterTimePicker } from '@pushly/aqe/lib/components/better-timepicker/better-timepicker'
import { DatePicker, Form, Radio, Skeleton, Space, Switch, Typography } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'

import './styles/app-message-schedule-builder.scss'

type AppMessageScheduleBuilderProps = {
    submitProps: ComponentProps<typeof Well>
}

const displayScheduleWindow = (value: ScheduleWindow) => {
    switch (value) {
        case ScheduleWindow.STANDARD:
            return 'Fixed'
        case ScheduleWindow.TIMEZONE:
            return 'Subscriber Time Zone'
    }
}

const getStrategyDescription = (
    runStrategy: ScheduleWindow,
    dateTimeZone: string,
    endDateEnabled: boolean,
    dateStartStr?: string,
    dateEndStr?: string,
) => {
    const descriptors: string[] = [`The app message will start on ${dateStartStr}`]
    if (endDateEnabled && dateEndStr) {
        descriptors.push(`and run through ${dateEndStr}`)
    }
    if (runStrategy === ScheduleWindow.TIMEZONE) {
        descriptors.push(`in each subscriber's time zone`)
    } else {
        descriptors.push(`in the ${dateTimeZone} time zone`)
    }

    return `${descriptors.join(' ')}.`
}
export const AppMessageScheduleBuilder = ({
    submitProps: { hideFooter, disableSubmit, onSubmit, onCancel, submitText },
}: AppMessageScheduleBuilderProps) => {
    const [context, dispatch] = useAppMessageBuilderContext()
    const { variants, selectedVariantIdx } = context

    const value = variants[selectedVariantIdx]

    const schedule = value.schedules?.[0] ?? new MutableAppMessageBuilderSchedulePayload()
    const { runDateWindow, runDateStartUtc, runDateEndUtc, runDateTimeZone } = schedule

    const now = dayjs()
    const today = now.clone().startOf('day')
    const dateStart = dayjs(runDateStartUtc)

    const [endDateEnabled, setEndDateEnabled] = React.useState(!!runDateEndUtc)
    const [displaySchedule, setDisplaySchedule] = React.useState<string>()

    const handleScheduleUpdate = (change: Partial<MutableAppMessageBuilderSchedulePayload>) => {
        const newSchedule = schedule.clone(change)

        if (change.runDateEndUtc === null) {
            newSchedule.runDateEndUtc = null
        }

        dispatch({ type: 'patch', entity: 'schedule', data: newSchedule })
    }

    const handleDateChange = (date: Dayjs | null, rangePoint: 'Start' | 'End') => {
        let nextDate = date
        const scheduleKey = `runDate${rangePoint}Utc`

        if (nextDate) {
            const prevDate = dayjs(schedule[scheduleKey])
            nextDate = nextDate.hour(prevDate.hour()).minute(prevDate.minute())
        }

        const startDate = !nextDate ? undefined : nextDate.utc().toISOString()
        handleScheduleUpdate({ [scheduleKey]: startDate })
    }

    const handleTimeChange = (time: dayjs.Dayjs | null, rangePoint: 'Start' | 'End') => {
        let nextDate
        const scheduleKey = `runDate${rangePoint}Utc`

        if (time) {
            nextDate = dayjs(schedule[scheduleKey])
            nextDate = nextDate.hour(time.hour()).minute(time.minute()).second(59)
        }

        const startDate = !nextDate ? undefined : nextDate.utc().toISOString()

        handleScheduleUpdate({ [scheduleKey]: startDate })
    }

    React.useEffect(() => {
        setDisplaySchedule(
            getStrategyDescription(
                runDateWindow,
                runDateTimeZone,
                endDateEnabled,
                dateStart.format(BASE_TIME_FORMAT_WITHOUT_TZ),
                runDateEndUtc ? dayjs(runDateEndUtc).format(BASE_TIME_FORMAT_WITHOUT_TZ) : undefined,
            ),
        )
    }, [schedule])

    return (
        <Well
            title="Schedule"
            className={getClassNames('app-message-schedule-builder')}
            footerMode="ghost"
            disableSubmit={disableSubmit}
            onSubmit={onSubmit}
            onCancel={onCancel}
            submitText={submitText}
        >
            <Skeleton loading={context.loading} active={true} avatar={true} title={false}>
                <Form.Item labelCol={{ span: 4 }} labelAlign="left" className="schedule-strategy" label="Strategy">
                    <Radio.Group
                        buttonStyle="solid"
                        className="schedule-strategy-options"
                        disabled={context.disabled}
                        onChange={(ev) => {
                            const strategy = ScheduleWindow[ev.target.value]
                            handleScheduleUpdate({ runDateWindow: strategy })
                        }}
                        size="small"
                        defaultValue={schedule.runDateWindow ?? ScheduleWindow.STANDARD}
                        options={Object.values(ScheduleWindow).map((w) => ({
                            label: displayScheduleWindow(w),
                            value: ScheduleWindow[w],
                        }))}
                        optionType="button"
                    />
                </Form.Item>

                <div className="run-dates-row">
                    <Space size="large">
                        <Form.Item className="delivery-date-start" label="Schedule Start" layout="vertical">
                            <Space className="delivery-senddate-picker">
                                <DatePicker
                                    className="delivery-date-picker"
                                    format="ddd, MMM DD, YYYY"
                                    placeholder="Start Date"
                                    disabled={context.disabled}
                                    disabledDate={(current) => !!current && current < today}
                                    value={!dateStart ? dayjs().startOf('day') : dateStart}
                                    onChange={(startDate) => {
                                        handleDateChange(startDate, 'Start')
                                    }}
                                />

                                <BetterTimePicker
                                    className="delivery-time-picker"
                                    format="h:mm a"
                                    use12Hours={true}
                                    placeholder="Time"
                                    value={!dateStart ? now.clone().startOf('day') : dateStart}
                                    disabled={context.disabled}
                                    onTimeChange={(startTime) => handleTimeChange(startTime, 'Start')}
                                />
                            </Space>
                        </Form.Item>

                        <Form.Item
                            className="delivery-date-end"
                            label={
                                <>
                                    <Switch
                                        size="small"
                                        checked={endDateEnabled}
                                        disabled={context.disabled}
                                        onChange={(checked) => {
                                            let endDate
                                            if (!checked) {
                                                endDate = null
                                            } else {
                                                const seed = dateStart ? dateStart.clone() : dayjs()

                                                seed.add(1, 'day').endOf('day')
                                                endDate = seed.format('YYYY-MM-DD HH:mm:ss')
                                            }

                                            handleScheduleUpdate({ runDateEndUtc: endDate })
                                            setEndDateEnabled(checked)
                                        }}
                                    />
                                    <span className="label-with-component-l">End Date</span>
                                </>
                            }
                            colon={false}
                            layout="vertical"
                        >
                            <Space className="delivery-senddate-picker">
                                <DatePicker
                                    className="delivery-date-picker"
                                    format="ddd, MMM DD, YYYY"
                                    placeholder="End Date"
                                    disabled={!endDateEnabled || context.disabled}
                                    disabledDate={(current) => !!dateStart && !!current && current < dateStart}
                                    value={!runDateEndUtc ? undefined : dayjs(runDateEndUtc)}
                                    onChange={(endDate) => handleDateChange(endDate, 'End')}
                                />

                                <BetterTimePicker
                                    className="delivery-time-picker"
                                    format="h:mm a"
                                    use12Hours={true}
                                    placeholder="Time"
                                    disabled={!endDateEnabled || context.disabled}
                                    disabledDate={(current) => !!dateStart && current < dateStart.clone().add(1, 'day')}
                                    value={!runDateEndUtc ? undefined : dayjs(runDateEndUtc)}
                                    onTimeChange={(endTime) => handleTimeChange(endTime, 'End')}
                                />
                            </Space>
                        </Form.Item>
                    </Space>

                    <Typography.Text type="secondary" className="schedule-description">
                        {displaySchedule}
                    </Typography.Text>
                </div>
            </Skeleton>
        </Well>
    )
}
